import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence } from "motion/react";
import { LogOut, Shield, RefreshCcw, Settings as SettingsIcon, LifeBuoy } from "lucide-react";
import PageTransition from "./components/PageTransition";
import { useAuth } from "../features/auth/context/AuthContext";
import { getLinksSummary, getStorageUsage } from "../features/vault/vaultApi";
import { listSharedWithMe } from "../features/vault/sharedApi";
import { listCollections } from "../features/collections/collectionsApi";
import { listApprovals } from "../features/approvals/approvalsApi";
import ImpersonationBanner from "./components/ImpersonationBanner";
import BroadcastBanner from "./components/BroadcastBanner";
import { VaultProvider, useApp } from "./vault/ctx";
import Topbar from "./vault/Topbar";
import Sidebar, { type SidebarData } from "./vault/Sidebar";
import SelBar from "./vault/SelBar";
import SvgDefs from "./vault/SvgDefs";
import VaultModals from "./vault/VaultModals";
import ClientView from "./vault/ClientView";

/** Sidebar+topbar shell for the Vault surfaces — 1:1 with the reference vault-app
 *  `.app > .topbar + .body(.sidebar + .main)` structure. Studio/Band/Engagement are
 *  full-screen and live OUTSIDE this layout (keeping their own editor theme). */
export default function VaultLayout() {
  return (
    <VaultProvider>
      <Shell />
    </VaultProvider>
  );
}

/** Bytes → short human string (mirrors StoragePage's local formatter). */
function gb(bytes: number): string {
  if (!bytes || bytes < 0) return "0 GB";
  const g = bytes / 1024 ** 3;
  if (g >= 1) return `${g.toFixed(1)} GB`;
  const m = bytes / 1024 ** 2;
  if (m >= 1) return `${m.toFixed(0)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function Shell() {
  const { user, logout, isSaasOwner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { navOpen, setNavOpen, client } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sideData, setSideData] = useState<SidebarData | undefined>(undefined);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setNavOpen(false); setMenuOpen(false); }, [location.pathname, location.search, setNavOpen]);

  // Real sidebar counts + storage — fetched once. Any call that fails leaves its
  // field undefined so Sidebar falls back to its demo default (never crashes on a
  // near-empty workspace). Nullish-coalescing in Sidebar keeps a real 0 as 0.
  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      listSharedWithMe(),
      getLinksSummary(),
      listCollections(),
      listApprovals(),
      getStorageUsage(),
    ]).then(([shared, links, collections, approvals, usage]) => {
      if (!alive) return;
      const counts = {
        shared: shared.status === "fulfilled" ? shared.value.length : undefined,
        links: links.status === "fulfilled" ? links.value.activeLinks : undefined,
        collections: collections.status === "fulfilled" ? collections.value.length : undefined,
        approvals: approvals.status === "fulfilled" ? approvals.value.filter((a) => a.status === "awaiting").length : undefined,
      };
      let storageText: SidebarData["storageText"];
      let storagePct: number | undefined;
      let connected: boolean | undefined;
      if (usage.status === "fulfilled") {
        const u = usage.value;
        connected = u.connected;
        storagePct = u.totalBytes > 0 ? Math.min(100, Math.round((u.usedBytes / u.totalBytes) * 100)) : 0;
        storageText = (
          <>
            {gb(u.usedBytes)}{u.totalBytes > 0 ? ` of ${gb(u.totalBytes)}` : ""} ·{" "}
            <span style={{ color: "var(--muted)" }}>{u.connected ? "connected to your S3" : "storage not connected"}</span>
          </>
        );
      }
      setSideData({ counts, storageText, storagePct, connected });
    });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const initials = (user?.name ?? "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="vaultui">
    <div className="app vault-surface">
      <SvgDefs />
      <div style={{ position: "relative" }} ref={menuRef}>
        <Topbar avatar={initials} avatarTitle={`${user?.name ?? ""} — ${user?.email ?? ""}`} onAvatar={() => setMenuOpen((v) => !v)} />
        {menuOpen && (
          <div className="save-pop" style={{ position: "absolute", top: 54, right: 16, width: 240, zIndex: 60 }}>
            <div className="sp-h">{user?.name}</div>
            <div style={{ padding: "0 10px 6px", fontSize: 11.5, color: "var(--muted)", wordBreak: "break-all" }}>{user?.email}</div>
            <button onClick={() => navigate("/settings")}><SettingsIcon size={14} /> Settings &amp; workspace</button>
            {isSaasOwner && <button onClick={() => navigate("/admin")}><Shield size={14} /> Admin Console</button>}
            <button onClick={() => navigate("/select-workspace")}><RefreshCcw size={14} /> Switch workspace</button>
            <button onClick={() => navigate("/contact")}><LifeBuoy size={14} /> Help &amp; support</button>
            <button onClick={() => { logout(); navigate("/"); }} style={{ color: "var(--red)" }}><LogOut size={14} /> Sign out</button>
          </div>
        )}
      </div>

      <div className="body">
        {navOpen && <div className="side-scrim" onClick={() => setNavOpen(false)} />}
        <Sidebar data={sideData} />
        <main className="main">
          <ImpersonationBanner />
          <BroadcastBanner />
          <section className="page active">
            <AnimatePresence mode="wait" initial={false}>
              <PageTransition key={location.pathname}><Outlet /></PageTransition>
            </AnimatePresence>
          </section>
        </main>
      </div>

      <SelBar />
      <VaultModals />
      {client && <ClientView />}
    </div>
    </div>
  );
}
